import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Briefcase, Plus, Trash2, Users, UserPlus, Calendar, ChevronRight, Phone, Mail, History, Receipt } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { EmailTemplatesSection } from '@/components/recruitment/EmailTemplatesSection';
import { ApplicantActivityDialog } from '@/components/recruitment/ApplicantActivityDialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTaxConfig } from '@/hooks/useTaxConfig';
import BranchTaxBadge from '@/components/BranchTaxBadge';
import { useBranchStore } from '@/stores/branchStore';
import { localToday } from '@/lib/dateRange';

const STAGES = ['applied', 'screened', 'interviewed', 'offered', 'hired', 'rejected'];
const STAGE_COLORS = { applied: 'bg-info/20 text-info', screened: 'bg-cyan-100 text-cyan-800', interviewed: 'bg-warning/20 text-warning', offered: 'bg-purple-100 text-purple-800', hired: 'bg-success/20 text-success', rejected: 'bg-error/20 text-error' };

export default function RecruitmentPage() {
  const [postings, setPostings] = useState([]);
  const { t } = useLanguage();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [batchSelection, setBatchSelection] = useState([]);
  const [activityDialog, setActivityDialog] = useState({ open: false, applicantId: '', applicantName: '' });
  const [showPostingDialog, setShowPostingDialog] = useState(false);
  const [showApplicantDialog, setShowApplicantDialog] = useState(false);
  const [showInterviewDialog, setShowInterviewDialog] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [postingForm, setPostingForm] = useState({ title: '', department: '', job_type: 'full_time', experience_required: '', salary_range_min: '', salary_range_max: '', description: '', requirements: '', positions_count: 1, closing_date: '' });
  const [appForm, setAppForm] = useState({ job_posting_id: '', name: '', email: '', phone: '', experience_years: '', expected_salary: '', notes: '', source: 'direct' });
  const [intForm, setIntForm] = useState({ date: '', time: '', interviewer: '', type: 'in_person', notes: '' });

  // Recruitment Fee state
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [feeForm, setFeeForm] = useState({ amount: '', fee_type: 'agency_fee', payment_mode: 'bank', branch_id: '', apply_vat: true, vat_inclusive: false, date: localToday() });
  const [feesForApplicant, setFeesForApplicant] = useState({ fees: [], total: 0 });
  const { branches, fetchBranches } = useBranchStore();
  const { taxConfig } = useTaxConfig(feeForm.branch_id);

  useEffect(() => { fetchBranches?.(); }, []);
  useEffect(() => {
    if (taxConfig && showFeeDialog) {
      setFeeForm(f => ({ ...f, apply_vat: !!taxConfig.vat_enabled, vat_inclusive: !!taxConfig.vat_inclusive }));
    }
  }, [taxConfig?.vat_enabled, taxConfig?.vat_inclusive, showFeeDialog]);

  const fetchData = async () => {
    try {
      const [pRes, aRes, sumRes] = await Promise.all([
        api.get('/job-postings'),
        api.get('/applicants'),
        api.get('/recruitment/pipeline-summary').catch(() => ({ data: null })),
      ]);
      setPostings(pRes.data);
      setApplicants(aRes.data);
      if (sumRes.data) setPipelineSummary(sumRes.data);
    } catch (err) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreatePosting = async (e) => {
    e.preventDefault();
    try {
      await api.post('/job-postings', { ...postingForm, salary_range_min: parseFloat(postingForm.salary_range_min) || 0, salary_range_max: parseFloat(postingForm.salary_range_max) || 0, positions_count: parseInt(postingForm.positions_count) || 1 });
      toast.success('Job posting created');
      setShowPostingDialog(false);
      setPostingForm({ title: '', department: '', job_type: 'full_time', experience_required: '', salary_range_min: '', salary_range_max: '', description: '', requirements: '', positions_count: 1, closing_date: '' });
      fetchData();
    } catch (err) { toast.error('Failed to create'); }
  };

  const handleCreateApplicant = async (e) => {
    e.preventDefault();
    if (!appForm.name) { toast.error('Name is required'); return; }
    try {
      await api.post('/applicants', { ...appForm, experience_years: parseInt(appForm.experience_years) || 0, expected_salary: parseFloat(appForm.expected_salary) || 0 });
      toast.success('Applicant added');
      setShowApplicantDialog(false);
      setAppForm({ job_posting_id: '', name: '', email: '', phone: '', experience_years: '', expected_salary: '', notes: '', source: 'direct' });
      fetchData();
    } catch (err) { toast.error('Failed to add applicant'); }
  };

  const handleMoveStage = async (appId, stage) => {
    try {
      await api.put(`/applicants/${appId}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
      fetchData();
    } catch (err) { toast.error('Failed to move'); }
  };

  const handleScheduleInterview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/applicants/${selectedApplicant.id}/interview`, intForm);
      toast.success('Interview scheduled');
      setShowInterviewDialog(false);
      setIntForm({ date: '', time: '', interviewer: '', type: 'in_person', notes: '' });
      fetchData();
    } catch (err) { toast.error('Failed to schedule'); }
  };

  const handleDeletePosting = async (id) => { if (window.confirm('Delete posting and all applicants?')) { await api.delete(`/job-postings/${id}`); fetchData(); } };
  const handleDeleteApplicant = async (id) => { if (window.confirm('Delete?')) { await api.delete(`/applicants/${id}`); fetchData(); } };

  const openFeeDialog = async (applicant) => {
    setSelectedApplicant(applicant);
    setFeeForm({ amount: '', fee_type: 'agency_fee', payment_mode: 'bank', branch_id: '', apply_vat: !!taxConfig?.vat_enabled, vat_inclusive: !!taxConfig?.vat_inclusive, date: localToday() });
    setShowFeeDialog(true);
    try {
      const res = await api.get(`/applicants/${applicant.id}/recruitment-fees`);
      setFeesForApplicant(res.data);
    } catch { setFeesForApplicant({ fees: [], total: 0 }); }
  };

  const handleRecordFee = async () => {
    if (!feeForm.amount || parseFloat(feeForm.amount) <= 0) { toast.error('Enter amount'); return; }
    try {
      await api.post(`/applicants/${selectedApplicant.id}/recruitment-fee`, {
        ...feeForm,
        amount: parseFloat(feeForm.amount),
        vat_rate: taxConfig?.vat_rate || 15,
      });
      toast.success('Fee recorded + expense auto-created');
      // Refresh list
      const res = await api.get(`/applicants/${selectedApplicant.id}/recruitment-fees`);
      setFeesForApplicant(res.data);
      setFeeForm(f => ({ ...f, amount: '' }));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteFee = async (feeId) => {
    if (!window.confirm('Delete this fee? The linked expense will also be removed.')) return;
    try {
      await api.delete(`/recruitment-fees/${feeId}`);
      const res = await api.get(`/applicants/${selectedApplicant.id}/recruitment-fees`);
      setFeesForApplicant(res.data);
      toast.success('Fee deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const openApplicants = applicants.filter(a => selectedPosting ? a.job_posting_id === selectedPosting.id : true);
  const pipelineCounts = STAGES.reduce((acc, s) => ({ ...acc, [s]: openApplicants.filter(a => a.stage === s).length }), {});

  const handleBatchAdvance = async (targetStage) => {
    if (batchSelection.length === 0) { toast.error('Select applicants first'); return; }
    try {
      const res = await api.post('/recruitment/batch-advance', { applicant_ids: batchSelection, target_stage: targetStage });
      toast.success(`${res.data.updated} applicant(s) moved to ${targetStage}`);
      setBatchSelection([]);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleBatchSelect = (id) => {
    setBatchSelection(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="recruitment-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200/50">

                <Briefcase size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">{t('recruitment_title') || 'Recruitment'}</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Job postings and applicant tracking</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">{t('recruitment_subtitle') || 'Job postings, applicant tracking, and interview management.'}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowPostingDialog(true)} data-testid="add-posting-btn"><Plus size={14} className="mr-1" /> New Job Posting</Button>
            <Button variant="outline" onClick={() => { setShowApplicantDialog(true); setAppForm({ ...appForm, job_posting_id: selectedPosting?.id || '' }); }} data-testid="add-applicant-btn"><UserPlus size={14} className="mr-1" /> Add Applicant</Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="stat-postings"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 rounded-lg"><Briefcase size={18} className="text-info" /></div><div><div className="text-2xl font-bold">{postings.filter(p => p.status === 'open').length}</div><div className="text-xs text-muted-foreground">Open Positions</div></div></div></CardContent></Card>
          <Card data-testid="stat-applicants"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-success/10 rounded-lg"><Users size={18} className="text-success" /></div><div><div className="text-2xl font-bold">{applicants.length}</div><div className="text-xs text-muted-foreground">Total Applicants</div></div></div></CardContent></Card>
          <Card data-testid="stat-interviews"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 rounded-lg"><Calendar size={18} className="text-warning" /></div><div><div className="text-2xl font-bold">{applicants.filter(a => a.stage === 'interviewed').length}</div><div className="text-xs text-muted-foreground">In Interview</div></div></div></CardContent></Card>
          <Card data-testid="stat-hired"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-purple-50 rounded-lg"><UserPlus size={18} className="text-purple-600" /></div><div><div className="text-2xl font-bold">{applicants.filter(a => a.stage === 'hired').length}</div><div className="text-xs text-muted-foreground">Hired</div></div></div></CardContent></Card>
        </div>

        <Tabs defaultValue="postings" className="space-y-4">
          <TabsList data-testid="recruit-tabs">
            <TabsTrigger value="postings">Job Postings</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="templates">Email Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="postings">
            <div className="space-y-3">
              {postings.length === 0 ? <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No job postings yet.</CardContent></Card> :
                postings.map(p => (
                  <Card key={p.id} className={`cursor-pointer hover:shadow-md transition-all ${selectedPosting?.id === p.id ? 'ring-2 ring-primary' : ''}`} onClick={() => setSelectedPosting(p)} data-testid={`posting-${p.id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm flex items-center gap-2">{p.title} <Badge variant={p.status === 'open' ? 'default' : 'secondary'} className="text-[10px]">{p.status}</Badge></div>
                          <div className="text-xs text-muted-foreground mt-0.5">{p.department} | {p.job_type?.replace('_', ' ')} | {p.positions_count} position(s) {p.closing_date && `| Closes: ${p.closing_date}`}</div>
                          {(p.salary_range_min > 0 || p.salary_range_max > 0) && <div className="text-xs text-muted-foreground">Salary: {p.salary_range_min?.toLocaleString()} - {p.salary_range_max?.toLocaleString()}</div>}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right"><div className="text-lg font-bold">{p.applicant_count || 0}</div><div className="text-[10px] text-muted-foreground">Applicants</div></div>
                          <Button size="sm" variant="ghost" className="text-error" onClick={(e) => { e.stopPropagation(); handleDeletePosting(p.id); }}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              }
            </div>
          </TabsContent>

          <TabsContent value="pipeline">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Applicant Pipeline {selectedPosting ? `- ${selectedPosting.title}` : '(All)'}</CardTitle>
                  {selectedPosting && <Button size="sm" variant="ghost" onClick={() => setSelectedPosting(null)}>Show All</Button>}
                </div>
              </CardHeader>
              <CardContent>
                {/* Pipeline Stage Counters */}
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {STAGES.map(s => (
                    <div key={s} className={`flex-1 min-w-[100px] rounded-lg p-2 text-center ${STAGE_COLORS[s]}`}>
                      <div className="text-lg font-bold">{pipelineCounts[s]}</div>
                      <div className="text-[10px] capitalize">{s}</div>
                    </div>
                  ))}
                </div>
                {openApplicants.length === 0 ? <div className="text-center py-6 text-sm text-muted-foreground">No applicants.</div> :
                  <div className="space-y-2">
                    {batchSelection.length > 0 && (
                      <div className="flex items-center gap-2 p-2 bg-info/10 rounded-lg border border-info/30" data-testid="batch-actions">
                        <span className="text-xs font-medium text-info">{batchSelection.length} selected</span>
                        <span className="text-xs text-muted-foreground">Move to:</span>
                        {STAGES.filter(s => s !== 'applied').map(s => (
                          <Button key={s} size="sm" variant="outline" className="h-6 text-[10px] capitalize" onClick={() => handleBatchAdvance(s)} data-testid={`batch-to-${s}`}>{s}</Button>
                        ))}
                        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setBatchSelection([])}>Clear</Button>
                      </div>
                    )}
                    {openApplicants.map(a => (
                      <div key={a.id} className={`border rounded-lg p-3 flex items-center justify-between hover:bg-muted/50 ${batchSelection.includes(a.id) ? 'ring-2 ring-blue-300 bg-info/10' : ''}`} data-testid={`applicant-${a.id}`}>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={batchSelection.includes(a.id)} onChange={() => toggleBatchSelect(a.id)} className="rounded" data-testid={`select-${a.id}`} />
                          <div>
                            <div className="font-medium text-sm">{a.name}</div>
                            <div className="flex gap-3 text-xs text-muted-foreground">
                              {a.email && <span className="flex items-center gap-0.5"><Mail size={10} /> {a.email}</span>}
                              {a.phone && <span className="flex items-center gap-0.5"><Phone size={10} /> {a.phone}</span>}
                              <span>{a.experience_years}yr exp</span>
                              <span className="capitalize">via {a.source}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select value={a.stage} onValueChange={(v) => handleMoveStage(a.id, v)}>
                            <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" onClick={() => { setSelectedApplicant(a); setShowInterviewDialog(true); }} title="Schedule Interview"><Calendar size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => openFeeDialog(a)} title="Record recruitment/agency fee" data-testid={`fee-btn-${a.id}`}><Receipt size={14} className="text-warning" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setActivityDialog({ open: true, applicantId: a.id, applicantName: a.name })} title="Activity Log"><History size={14} /></Button>
                          <Button size="sm" variant="ghost" className="text-error" onClick={() => handleDeleteApplicant(a.id)}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            {pipelineSummary ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit">{pipelineSummary.total_applicants}</div><div className="text-xs text-muted-foreground">Total Applicants</div></CardContent></Card>
                  <Card><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit text-info">{pipelineSummary.active_postings}</div><div className="text-xs text-muted-foreground">Active Postings</div></CardContent></Card>
                  <Card><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit text-warning">{pipelineSummary.total_open_positions}</div><div className="text-xs text-muted-foreground">Open Positions</div></CardContent></Card>
                  <Card><CardContent className="pt-4 pb-3"><div className="text-2xl font-bold font-outfit text-success">{pipelineSummary.hired_count}</div><div className="text-xs text-muted-foreground">Hired</div></CardContent></Card>
                </div>
                {pipelineSummary.conversion_rates && Object.keys(pipelineSummary.conversion_rates).length > 0 && (
                  <Card data-testid="conversion-rates">
                    <CardHeader><CardTitle className="text-sm">Conversion Funnel</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {['screened', 'interviewed', 'offered', 'hired'].map(stage => (
                          <div key={stage} className="flex items-center gap-3">
                            <span className="text-xs capitalize w-24">{stage}</span>
                            <div className="flex-1 bg-muted rounded-full h-3">
                              <div className="bg-info h-3 rounded-full transition-all" style={{ width: `${Math.min(100, pipelineSummary.conversion_rates[stage] || 0)}%` }} />
                            </div>
                            <span className="text-xs font-medium w-12 text-right">{pipelineSummary.conversion_rates[stage] || 0}%</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {pipelineSummary.top_postings?.length > 0 && (
                  <Card data-testid="top-postings">
                    <CardHeader><CardTitle className="text-sm">Top Active Postings</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {pipelineSummary.top_postings.map((p, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 border">
                            <span className="text-sm font-medium">{p.title}</span>
                            <div className="flex gap-2 text-xs">
                              <Badge variant="outline">{p.applicants} applicants</Badge>
                              <Badge variant="secondary">{p.positions} position{p.positions > 1 ? 's' : ''}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No analytics data available</CardContent></Card>
            )}
          </TabsContent>

          <TabsContent value="templates">
            <EmailTemplatesSection />
          </TabsContent>
        </Tabs>

        <ApplicantActivityDialog
          applicantId={activityDialog.applicantId}
          applicantName={activityDialog.applicantName}
          open={activityDialog.open}
          onOpenChange={(open) => setActivityDialog(prev => ({ ...prev, open }))}
        />

        {/* Create Posting Dialog */}
        <Dialog open={showPostingDialog} onOpenChange={setShowPostingDialog}>
          <DialogContent className="max-w-lg" data-testid="posting-dialog">
            <DialogHeader><DialogTitle>New Job Posting</DialogTitle></DialogHeader>
            <form onSubmit={handleCreatePosting} className="space-y-3 max-h-[60dvh] overflow-y-auto pr-1">
              <div><Label>Job Title *</Label><Input value={postingForm.title} onChange={(e) => setPostingForm({ ...postingForm, title: e.target.value })} data-testid="posting-title" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Department</Label><Input value={postingForm.department} onChange={(e) => setPostingForm({ ...postingForm, department: e.target.value })} /></div>
                <div><Label>Job Type</Label>
                  <Select value={postingForm.job_type} onValueChange={(v) => setPostingForm({ ...postingForm, job_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="full_time">Full Time</SelectItem><SelectItem value="part_time">Part Time</SelectItem><SelectItem value="contract">Contract</SelectItem><SelectItem value="intern">Internship</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Salary Min</Label><Input type="number" value={postingForm.salary_range_min} onChange={(e) => setPostingForm({ ...postingForm, salary_range_min: e.target.value })} /></div>
                <div><Label>Salary Max</Label><Input type="number" value={postingForm.salary_range_max} onChange={(e) => setPostingForm({ ...postingForm, salary_range_max: e.target.value })} /></div>
                <div><Label>Positions</Label><Input type="number" min="1" value={postingForm.positions_count} onChange={(e) => setPostingForm({ ...postingForm, positions_count: e.target.value })} /></div>
                <div><Label>Closing Date</Label><Input type="date" value={postingForm.closing_date} onChange={(e) => setPostingForm({ ...postingForm, closing_date: e.target.value })} /></div>
              </div>
              <div><Label>Experience Required</Label><Input value={postingForm.experience_required} onChange={(e) => setPostingForm({ ...postingForm, experience_required: e.target.value })} placeholder="e.g. 3-5 years" /></div>
              <div><Label>Description</Label><Textarea value={postingForm.description} onChange={(e) => setPostingForm({ ...postingForm, description: e.target.value })} rows={3} /></div>
              <div><Label>Requirements</Label><Textarea value={postingForm.requirements} onChange={(e) => setPostingForm({ ...postingForm, requirements: e.target.value })} rows={2} placeholder="One per line" /></div>
              <Button type="submit" className="w-full" data-testid="posting-submit">Create Posting</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Applicant Dialog */}
        <Dialog open={showApplicantDialog} onOpenChange={setShowApplicantDialog}>
          <DialogContent data-testid="applicant-dialog">
            <DialogHeader><DialogTitle>Add Applicant</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateApplicant} className="space-y-3">
              <div><Label>Job Position</Label>
                <Select value={appForm.job_posting_id || 'none'} onValueChange={(v) => setAppForm({ ...appForm, job_posting_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="app-posting"><SelectValue placeholder="Select position" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">General</SelectItem>{postings.filter(p => p.status === 'open').map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input value={appForm.name} onChange={(e) => setAppForm({ ...appForm, name: e.target.value })} data-testid="app-name" required /></div>
                <div><Label>Email</Label><Input type="email" value={appForm.email} onChange={(e) => setAppForm({ ...appForm, email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={appForm.phone} onChange={(e) => setAppForm({ ...appForm, phone: e.target.value })} /></div>
                <div><Label>Experience (years)</Label><Input type="number" value={appForm.experience_years} onChange={(e) => setAppForm({ ...appForm, experience_years: e.target.value })} /></div>
                <div><Label>Expected Salary</Label><Input type="number" value={appForm.expected_salary} onChange={(e) => setAppForm({ ...appForm, expected_salary: e.target.value })} /></div>
                <div><Label>Source</Label>
                  <Select value={appForm.source} onValueChange={(v) => setAppForm({ ...appForm, source: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="direct">Direct</SelectItem><SelectItem value="linkedin">LinkedIn</SelectItem><SelectItem value="referral">Referral</SelectItem><SelectItem value="agency">Agency</SelectItem><SelectItem value="website">Website</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea value={appForm.notes} onChange={(e) => setAppForm({ ...appForm, notes: e.target.value })} rows={2} /></div>
              <Button type="submit" className="w-full" data-testid="app-submit">Add Applicant</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Schedule Interview Dialog */}
        <Dialog open={showInterviewDialog} onOpenChange={setShowInterviewDialog}>
          <DialogContent data-testid="interview-dialog">
            <DialogHeader><DialogTitle>Schedule Interview - {selectedApplicant?.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleScheduleInterview} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={intForm.date} onChange={(e) => setIntForm({ ...intForm, date: e.target.value })} required /></div>
                <div><Label>Time</Label><Input type="time" value={intForm.time} onChange={(e) => setIntForm({ ...intForm, time: e.target.value })} /></div>
                <div><Label>Interviewer</Label><Input value={intForm.interviewer} onChange={(e) => setIntForm({ ...intForm, interviewer: e.target.value })} /></div>
                <div><Label>Type</Label>
                  <Select value={intForm.type} onValueChange={(v) => setIntForm({ ...intForm, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="in_person">In Person</SelectItem><SelectItem value="phone">Phone</SelectItem><SelectItem value="video">Video Call</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea value={intForm.notes} onChange={(e) => setIntForm({ ...intForm, notes: e.target.value })} rows={2} /></div>
              <Button type="submit" className="w-full" data-testid="interview-submit">Schedule Interview</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Recruitment Fee Dialog */}
        <Dialog open={showFeeDialog} onOpenChange={setShowFeeDialog}>
          <DialogContent className="max-w-2xl" data-testid="fee-dialog">
            <DialogHeader>
              <DialogTitle>
                Recruitment Fee — {selectedApplicant?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Existing fees */}
              {feesForApplicant.fees?.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-2 border-b border-border flex justify-between text-xs font-semibold text-muted-foreground">
                    <span>Previous fees ({feesForApplicant.fees.length})</span>
                    <span>Total: {cur()} {(feesForApplicant.total || 0).toLocaleString()}</span>
                  </div>
                  <div className="divide-y divide-border max-h-40 overflow-y-auto">
                    {feesForApplicant.fees.map(f => (
                      <div key={f.id} className="flex items-center justify-between px-3 py-2 text-xs">
                        <div>
                          <span className="font-medium text-foreground capitalize">{f.fee_type.replace(/_/g, ' ')}</span>
                          <span className="text-muted-foreground ml-2">{new Date(f.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">{cur()} {f.total.toLocaleString()}</span>
                          <span className="text-muted-foreground text-[10px]">VAT {f.vat_rate}%</span>
                          <button onClick={() => handleDeleteFee(f.id)} className="text-error hover:text-error" data-testid={`delete-fee-${f.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New fee form */}
              <div className="p-3 bg-warning/10 border-2 border-warning/30 rounded-lg space-y-3">
                <p className="text-xs font-semibold text-warning">Record New Fee</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Fee Type</Label>
                    <Select value={feeForm.fee_type} onValueChange={(v) => setFeeForm({ ...feeForm, fee_type: v })}>
                      <SelectTrigger data-testid="fee-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agency_fee">Agency / Recruiter Fee</SelectItem>
                        <SelectItem value="placement">Placement Fee</SelectItem>
                        <SelectItem value="onboarding">Onboarding / Training</SelectItem>
                        <SelectItem value="visa">Visa Fee</SelectItem>
                        <SelectItem value="iqama">Iqama / Work Permit</SelectItem>
                        <SelectItem value="medical">Medical Exam</SelectItem>
                        <SelectItem value="ticket">Flight Ticket</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Amount (SR) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={feeForm.amount}
                      onChange={(e) => setFeeForm({ ...feeForm, amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="fee-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Branch</Label>
                    <Select value={feeForm.branch_id || 'none'} onValueChange={(v) => setFeeForm({ ...feeForm, branch_id: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="fee-branch-select"><SelectValue placeholder="No branch" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No branch</SelectItem>
                        {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {feeForm.branch_id && (
                      <div className="mt-1.5">
                        <BranchTaxBadge branchId={feeForm.branch_id} compact />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Payment Mode</Label>
                    <Select value={feeForm.payment_mode} onValueChange={(v) => setFeeForm({ ...feeForm, payment_mode: v })}>
                      <SelectTrigger data-testid="fee-payment-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank">Bank Transfer</SelectItem>
                        <SelectItem value="credit">Credit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={feeForm.date} onChange={(e) => setFeeForm({ ...feeForm, date: e.target.value })} />
                  </div>
                </div>

                {/* VAT - auto-filled from tax config */}
                <div className="flex items-center gap-3 p-2 bg-white rounded border border-border flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={feeForm.apply_vat}
                      onChange={(e) => setFeeForm({ ...feeForm, apply_vat: e.target.checked })}
                      data-testid="fee-vat-toggle"
                    />
                    <span className="font-medium">Apply VAT</span>
                  </label>
                  {feeForm.apply_vat && taxConfig && (
                    <>
                      <span className="text-xs font-semibold text-info">{taxConfig.vat_rate}%</span>
                      <div className="flex items-center gap-1 text-[10px] ml-auto">
                        <span className="text-muted-foreground">Price is:</span>
                        <button type="button" onClick={() => setFeeForm({ ...feeForm, vat_inclusive: false })} className={`px-2 py-0.5 rounded ${!feeForm.vat_inclusive ? 'bg-info text-white' : 'bg-muted'}`} data-testid="fee-vat-exclusive">Exclusive</button>
                        <button type="button" onClick={() => setFeeForm({ ...feeForm, vat_inclusive: true })} className={`px-2 py-0.5 rounded ${feeForm.vat_inclusive ? 'bg-info text-white' : 'bg-muted'}`} data-testid="fee-vat-inclusive">Inclusive</button>
                      </div>
                    </>
                  )}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground w-full mt-1">
                    {taxConfig?.country_name || '—'} • {taxConfig?.source === 'branch_override' ? 'Branch override' : 'Tenant default'}
                  </span>
                </div>

                {/* Preview */}
                {feeForm.amount && parseFloat(feeForm.amount) > 0 && feeForm.apply_vat && taxConfig?.vat_rate > 0 && (
                  <div className="text-xs text-muted-foreground p-2 bg-white rounded border border-border">
                    {(() => {
                      const amt = parseFloat(feeForm.amount);
                      const rate = taxConfig.vat_rate;
                      if (feeForm.vat_inclusive) {
                        const net = amt / (1 + rate / 100);
                        const vat = amt - net;
                        return <span>Net: <b>{cur()} {net.toFixed(2)}</b> + VAT: <b className="text-info">{cur()} {vat.toFixed(2)}</b> = Total: <b>{cur()} {amt.toFixed(2)}</b></span>;
                      } else {
                        const vat = amt * rate / 100;
                        return <span>Net: {cur()} {amt.toFixed(2)} + VAT: <b className="text-info">{cur()} {vat.toFixed(2)}</b> = Total: <b>{cur()} {(amt + vat).toFixed(2)}</b></span>;
                      }
                    })()}
                  </div>
                )}

                <Button onClick={handleRecordFee} className="w-full" data-testid="save-fee-btn">
                  <Plus size={14} className="mr-1" /> Record Fee (auto-creates expense)
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
